import { render, h } from 'preact';
import { App } from './App';
import { styles } from './styles';
import type { HostAdapter } from '@pinmerge/shared';

export type { HostAdapter } from '@pinmerge/shared';

const HOST_ID = 'pinmerge-host';

/** Runtime config the widget needs to render. Structurally identical to the
 *  script-tag channel's BootConfig (main.ts) — kept here so the package channel
 *  can construct one without importing from the script-tag entry. */
export interface WidgetConfig {
  token: string;
  apiUrl: string;
  /** No token + no apiUrl → demo mode: submit logs to console, no network call. */
  demoMode: boolean;
  /** URL JWT carried in ?pinmerge_auth=… by the magic-link callback. */
  initialAuthHandoffJwt: string | null;
  /** Prototype-version slug from ?pinmerge_v=. */
  versionSlug: string | null;
}

export interface CreateWidgetOptions {
  /** Host-application adapter. Only threaded through for now — not consumed
   *  yet (wiring lands in a later WP). Absent in the script-tag channel. */
  adapter?: HostAdapter;
  /** Host element to render into. When omitted (script-tag channel) createWidget
   *  creates its own fixed, top-layer host div and appends it to the document. */
  hostEl?: HTMLElement;
  /** Channel-specific exit hook, run before the DOM teardown when the in-widget
   *  exit action fires. The script-tag channel injects setReviewActive(false)
   *  here; the package channel leaves it undefined so unmount never touches the
   *  localStorage review gate. */
  onExit?: () => void;
}

export interface WidgetHandle {
  destroy(): void;
}

// Mounts the widget into its own Shadow Root and renders <App>. This is the
// single shared entry point for both channels: the script-tag boot path and a
// future React wrapper. It encapsulates *only* mount + render + teardown and
// assumes the widget is already meant to be active — channel-specific gating
// (review-mode dormancy, URL activation), the window.__pinMerge stamping, the
// HOST_ID idempotency guard and the history patch stay in the caller (boot()).
export function createWidget(
  config: WidgetConfig,
  options: CreateWidgetOptions = {},
): WidgetHandle {
  const host =
    options.hostEl ??
    (() => {
      const el = document.createElement('div');
      el.id = HOST_ID;
      el.style.cssText = 'all: initial; position: fixed; z-index: 2147483647;';
      document.documentElement.appendChild(el);
      return el;
    })();

  const shadow = host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  style.textContent = styles;
  shadow.appendChild(style);

  const root = document.createElement('div');
  shadow.appendChild(root);

  const destroy = () => {
    render(null, root);
    host.remove();
  };

  const onExit = () => {
    options.onExit?.();
    destroy();
  };

  render(h(App, { config, onExit, adapter: options.adapter }), root);

  return { destroy };
}
